import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DeleteActiveListDialog
 * Prompts user to choose what happens to tasks in a list being deleted.
 *  - move: reassign all tasks to a target list
 *  - deactivate: send all tasks back to Storage
 */
export default function DeleteActiveListDialog({
  open,
  onOpenChange,
  listToDelete,
  allLists,
  taskCount,
  onDeleted,
}) {
  const [mode, setMode] = useState('move');
  const [targetListId, setTargetListId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const otherLists = (allLists || []).filter(
    (l) => listToDelete && l.id !== listToDelete.id
  );

  useEffect(() => {
    if (open) {
      setMode('move');
      setTargetListId(otherLists[0]?.id || '');
    }
  }, [open, listToDelete]);  // eslint-disable-line

  if (!listToDelete) return null;

  const handleDelete = async () => {
    if (mode === 'move' && !targetListId) {
      toast.error('Please pick a target list');
      return;
    }
    setSubmitting(true);
    try {
      await axios.delete(`${API}/active_lists/${listToDelete.id}`, {
        data: {
          mode,
          target_list_id: mode === 'move' ? targetListId : null,
        },
        withCredentials: true,
      });
      toast.success(`List "${listToDelete.name}" deleted`);
      if (onDeleted) onDeleted();
      onOpenChange(false);
    } catch (error) {
      console.error('Error deleting list:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete list');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-active-list-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="h-font">Delete "{listToDelete.name}"?</DialogTitle>
        </DialogHeader>

        {taskCount > 0 ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This list contains <strong>{taskCount}</strong> active task
              {taskCount === 1 ? '' : 's'}. Choose what to do with them:
            </p>

            <RadioGroup value={mode} onValueChange={setMode} className="space-y-3">
              <div className="flex items-start gap-3">
                <RadioGroupItem value="move" id="delete-list-mode-move" data-testid="delete-list-mode-move" />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="delete-list-mode-move" className="cursor-pointer">
                    Move tasks to another list
                  </Label>
                  {mode === 'move' && (
                    <Select value={targetListId} onValueChange={setTargetListId}>
                      <SelectTrigger data-testid="delete-list-target-select">
                        <SelectValue placeholder="Select target list" />
                      </SelectTrigger>
                      <SelectContent>
                        {otherLists.map((l) => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <RadioGroupItem value="deactivate" id="delete-list-mode-deactivate" data-testid="delete-list-mode-deactivate" />
                <Label htmlFor="delete-list-mode-deactivate" className="cursor-pointer">
                  Deactivate tasks (send back to Storage)
                </Label>
              </div>
            </RadioGroup>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            This list is empty. Click Delete to confirm.
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="delete-active-list-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={submitting}
            style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
            data-testid="delete-active-list-confirm"
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
